import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Today from "./pages/Today";
import Products from "./pages/Products";
import Recipes from "./pages/Recipes";
import MenuPage from "./pages/Menu";
import Invoices from "./pages/Invoices";
import Suppliers from "./pages/Suppliers";
import SettingsPage from "./pages/Settings";
import PublicMenu from "./pages/PublicMenu";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import SuperSimulator from "./pages/SuperSimulator";
import { Mentions, Confidentialite, CGU } from "./pages/Legal";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./pages/AuthCallback";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./App.css";

function HomeGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Chargement…</div>;
  return user ? <Navigate to="/aujourdhui" replace /> : <Landing />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

/**
 * Détection synchrone du callback Emergent Auth AVANT tout ProtectedRoute :
 * si le fragment de l'URL contient `session_id=`, on rend `<AuthCallback>`
 * qui échange le session_id contre le cookie httpOnly puis redirige.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRoutes() {
  const location = useLocationCompat();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<HomeGate />} />
      <Route path="/connexion" element={<Login />} />
      <Route path="/inscription" element={<Signup />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/mentions-legales" element={<Mentions />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/carte/:slug" element={<PublicMenu />} />
      <Route path="/bienvenue" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/paiement/succes" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/paiement/annule" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />

      {/* Private — everything under Layout */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/aujourdhui" element={<Today />} />
        <Route path="factures" element={<Invoices />} />
        <Route path="factures/:id" element={<Invoices />} />
        <Route path="factures/:id/verification" element={<Invoices />} />
        <Route path="produits" element={<Products />} />
        <Route path="plats" element={<Recipes />} />
        <Route path="recettes" element={<Navigate to="/plats" replace />} />
        <Route path="ventes" element={<Navigate to="/aujourdhui" replace />} />
        <Route path="menus" element={<MenuPage />} />
        <Route path="menu" element={<Navigate to="/menus" replace />} />
        <Route path="fournisseurs" element={<Suppliers />} />
        <Route path="simulations" element={<Navigate to="/aujourdhui" replace />} />
        <Route path="parametres" element={<SettingsPage />} />
        <Route path="admin/simulateur" element={<SuperSimulator />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Hook local — évite un import supplémentaire en tête de fichier.
function useLocationCompat() {
  return useLocation();
}
